// react
import React, { useState, useEffect, useCallback } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// context
import { UserAuth } from "context/AuthContext";

// mui
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { CloseOutlined as CloseOutlinedIcon } from "@mui/icons-material";

// ui-components
import { TriggersTable } from "./TriggersTable";
import { EventsTable } from "./EventsTable";
import { ContactsTable } from "./ContactsTable";

// databox-controller
import { createAlarmTrigger, deleteAlarmTrigger } from "databox-controller/manageAlarm";
import fetchAlarmTriggers from "databox-controller/fetchAlarmTriggers";
import updateAlarmTrigger from "databox-controller/updateAlarmTrigger";
import fetchAlarmEvents from "databox-controller/fetchAlarmEvents";
import { fetchAlarmContacts, addAlarmContact, updateAlarmContact, deleteAlarmContact } from "databox-controller/manageAlarmContacts";

// record-controller
import fetchSensorAndMagnitudeData from "record-controller/fetchSensorAndMagnitudeData";

// firebase
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "config/firebase";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const AlarmViewer = ({ tag, isTagGroupMember }) => {
  const { t } = useTranslation();
  const { user } = UserAuth();

  const [triggers, setTriggers] = useState([]);
  const [alarmEvents, setAlarmEvents] = useState([]);
  const [alarmContacts, setAlarmContacts] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newTrigger, setNewTrigger] = useState({ source: "", magnitude: "", operator: "", value: "", unit: "", enabled: true });
  const [sensors, setSensors] = useState([]);
  const [magnitudes, setMagnitudes] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: "", last_name: "", phone: "", enabled: true });

  const fetchData = useCallback(async () => {
    try {
      const alarmTriggers = await fetchAlarmTriggers(tag.id);
      setTriggers(alarmTriggers);

      const { sensors, magnitudes } = await fetchSensorAndMagnitudeData(tag.id, isTagGroupMember);
      setSensors(sensors);
      setMagnitudes(magnitudes);

      const events = await fetchAlarmEvents(tag.id, 25);
      setAlarmEvents(events);

      const contacts = await fetchAlarmContacts(tag.id);
      setAlarmContacts(contacts);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [tag, isTagGroupMember]);

  useEffect(() => {
    fetchData();
  }, [tag.id, isTagGroupMember]);

  useEffect(() => {
    const currentTimestampInSeconds = Math.floor(Date.now() / 1000);

    const contactsRef = collection(db, "tagsdata", tag.id, "alarm_contacts");
    const qContacts = query(contactsRef, where("created_at", ">", currentTimestampInSeconds.toString()));

    const unsubscribeContactsCreation = onSnapshot(qContacts, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const newContact = { id: change.doc.id, ...change.doc.data() };
          setAlarmContacts((prev) => [newContact, ...prev]);
        }
      });
    });

    const unsubscribeContactsDeletion = onSnapshot(contactsRef, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") {
          const removedContactId = change.doc.id;
          setAlarmContacts((prev) => prev.filter((contact) => contact.id !== removedContactId));
        }
      });
    });

    const triggersRef = collection(db, "tagsdata", tag.id, "triggers");
    const qTriggers = query(triggersRef, where("createdAt", ">", currentTimestampInSeconds.toString()));

    const unsubscribeTriggersCreation = onSnapshot(qTriggers, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const newTrigger = { id: change.doc.id, ...change.doc.data() };
          setTriggers((prev) => [newTrigger, ...prev]);
        }
      });
    });

    const unsubscribeTriggersDeletion = onSnapshot(triggersRef, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") {
          const removedTriggerId = change.doc.id;
          setTriggers((prev) => prev.filter((trigger) => trigger.id !== removedTriggerId));
        }
      });
    });

    const eventsRef = collection(db, "tagsdata", tag.id, "alarm_events");
    const qEvents = query(eventsRef, where("timestamp", ">", currentTimestampInSeconds.toString()));

    const unsubscribeEvents = onSnapshot(qEvents, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === "added") {
          const events = await fetchAlarmEvents(tag.id, 25);
          setAlarmEvents(events);
        }
      });
    });

    return () => {
      unsubscribeContactsCreation();
      unsubscribeContactsDeletion();
      unsubscribeTriggersCreation();
      unsubscribeTriggersDeletion();
      unsubscribeEvents();
    };
  }, [tag, isTagGroupMember]);

  const handleAddTrigger = async () => {
    try {
      const trigger = {
        source: newTrigger.source,
        sourceName: sensors.find((sensor) => sensor.id === newTrigger.source)?.name || "",
        magnitude: newTrigger.magnitude,
        operator: newTrigger.operator,
        value: newTrigger.value,
        unit: newTrigger.unit,
        enabled: newTrigger.enabled,
      };

      await createAlarmTrigger(tag.id, user.uid, trigger);
      handleReset();
      showSnackbar(t("trigger_added_success"), "success");
    } catch (error) {
      console.error("Failed to add trigger:", error);
      showSnackbar(t("trigger_added_error"), "error");
    }
  };

  const handleDeleteTrigger = async (id) => {
    try {
      await deleteAlarmTrigger(tag.id, id);
      showSnackbar(t("trigger_deleted_success"), "success");
    } catch (error) {
      console.error("Failed to delete trigger:", error);
      showSnackbar(t("trigger_deleted_error"), "error");
    }
  };

  const handleToggleEnabled = async (id, enabled) => {
    try {
      await updateAlarmTrigger(tag.id, id, { enabled: !enabled });
      setTriggers((prev) => prev.map((trigger) => (trigger.id === id ? { ...trigger, enabled: !enabled } : trigger)));
      showSnackbar(t(enabled ? "trigger_disabled" : "trigger_enabled"), "success");
    } catch (error) {
      console.error("Failed to update trigger:", error);
      showSnackbar(t("trigger_update_error"), "error");
    }
  };

  const handleReset = () => {
    setOpenDialog(false);
    setNewTrigger({ source: "", magnitude: "", operator: "", value: "", unit: "", enabled: true });
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleAddContact = async () => {
    try {
      await addAlarmContact(tag.id, newContact);
      showSnackbar(t("recipient_add_success"), "success");
      setNewContact({ first_name: "", last_name: "", phone: "", enabled: true });
      setOpenContactDialog(false);
    } catch (error) {
      showSnackbar(t("recipient_add_error"), "error");
    }
  };

  const handleToggleContactEnabled = async (contactId, enabled) => {
    try {
      await updateAlarmContact(tag.id, contactId, { enabled: !enabled });
      setAlarmContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, enabled: !enabled } : c)));
      const message = enabled ? t("recipient_disabled") : t("recipient_enabled");
      const severity = enabled ? "info" : "success";
      showSnackbar(message, severity);
    } catch (error) {
      showSnackbar(t("recipient_update_error"), "error");
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await deleteAlarmContact(tag.id, contactId);
      showSnackbar(t("recipient_delete_success"), "success");
    } catch {
      showSnackbar(t("recipient_delete_error"), "error");
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ContactsTable contacts={alarmContacts} onToggleEnabled={handleToggleContactEnabled} onDeleteContact={handleDeleteContact} handleOpen={() => setOpenContactDialog(true)} />
        </Grid>

        <Grid item xs={12}>
          <TriggersTable triggersData={triggers} onToggleEnabled={handleToggleEnabled} onDelete={handleDeleteTrigger} handleOpen={() => setOpenDialog(true)} />
        </Grid>

        <Grid item xs={12}>
          <EventsTable eventsData={alarmEvents} />
        </Grid>
      </Grid>

      <Dialog
        open={openContactDialog}
        onClose={() => setOpenContactDialog(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" fontWeight="bold">
                {t("new_recipient")}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title={t("close")} placement="top">
                <IconButton onClick={() => setOpenContactDialog(false)} color="error" edge="end">
                  <CloseOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="first_name"
                name="first_name"
                label={t("first_name")}
                fullWidth
                value={newContact.first_name}
                onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="last_name"
                name="last_name"
                label={t("last_name")}
                fullWidth
                value={newContact.last_name}
                onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="phone"
                name="phone"
                label={t("phone")}
                required
                fullWidth
                value={newContact.phone}
                placeholder="+393471234567"
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button sx={getButtonStyle("close")} onClick={() => setOpenContactDialog(false)}>
            {t("cancel")}
          </Button>
          <Button variant="outlined" sx={getButtonStyle("open")} onClick={handleAddContact}>
            {t("add")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={handleReset}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" fontWeight="bold">
                {t("new_trigger")}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title={t("close")} placement="top">
                <IconButton onClick={handleReset} color="error" edge="end">
                  <CloseOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="source_name">{t("source_name")}</InputLabel>
                <Select
                  labelId={t("source_name")}
                  id="source_name"
                  value={newTrigger.source}
                  onChange={(e) => setNewTrigger({ ...newTrigger, source: e.target.value })}
                  input={<OutlinedInput label={t("source_name")} />}
                >
                  {sensors.map((sensor) => (
                    <MenuItem key={sensor.id} value={sensor.id}>
                      {sensor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="magnitude">{t("magnitude")}</InputLabel>
                <Select
                  labelId={t("magnitude")}
                  id="magnitude"
                  value={newTrigger.magnitude}
                  onChange={(e) => setNewTrigger({ ...newTrigger, magnitude: e.target.value })}
                  input={<OutlinedInput label={t("magnitude")} />}
                >
                  {magnitudes.map((magnitude) => (
                    <MenuItem key={magnitude} value={magnitude}>
                      {magnitude}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="operator-label">{t("operator")}</InputLabel>
                <Select
                  labelId="operator-label"
                  id="operator"
                  value={newTrigger.operator}
                  onChange={(e) => setNewTrigger({ ...newTrigger, operator: e.target.value })}
                  input={<OutlinedInput label={t("operator")} />}
                >
                  <MenuItem value=">">{">"}</MenuItem>
                  <MenuItem value="<">{"<"}</MenuItem>
                  <MenuItem value=">=">{">="}</MenuItem>
                  <MenuItem value="<=">{"<="}</MenuItem>
                  <MenuItem value="==">{"=="}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label={t("value")} value={newTrigger.value} onChange={(e) => setNewTrigger({ ...newTrigger, value: e.target.value })} placeholder={"30"} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button sx={getButtonStyle("close")} onClick={() => setOpenDialog(false)}>
            {t("cancel")}
          </Button>
          <Button variant="outlined" sx={getButtonStyle("open")} onClick={handleAddTrigger}>
            {t("add")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled" sx={{ borderRadius: 4 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlarmViewer;
